import React, { useEffect, useState } from 'react'
import { useStaticQuery, graphql } from 'gatsby'

// Hooks
import { useUserTimeZone } from '../hooks/useUserTimeZone'

// Components
import EmblaCarousel from './embla/emblaCarousel'

const AuctionCalendar = ({ slice, sliceContext }) => {
  const [auctions, setAuctions] = useState(sliceContext.auctions)
  const initialUuids = new Set(sliceContext.auctions.map((a) => a.auctionUuid))
  const { formatTimestampForUser } = useUserTimeZone()
  const [selectedCountry, setSelectedCountry] = useState('')
  // const [searchQuery, setSearchQuery] = useState('')
  const BidJSQuery = useStaticQuery(graphql`
    query BidJSTabQuery {
      prismicConfig {
        ...PrismicConfigFragment
      }
    }
  `)
  const configData = BidJSQuery.prismicConfig.data

  useEffect(() => {
    const apiURL = `https://${process.env.GATSBY_BIDJS_SERVER}.${process.env.GATSBY_BIDJS_REGION}.${process.env.GATSBY_BIDJS_DOMAIN}/auction-007/api/v1/`

    const fetchData = async () => {
      try {
        let homeURL = `${apiURL}home`
        const headers = {
          'x-forwarded-client-id': process.env.GATSBY_BIDJS_CLIENT_ID
        }
        const homeRes = await fetch(homeURL, { headers })
        const homeData = await homeRes.json()

        const upcomingFeatured = homeData.models?.HomePageModel?.upcomingModel?.upcomingFeaturedAuctions || []
        const upcoming = homeData.models?.HomePageModel?.upcomingModel?.upcomingAuctions || []
        upcomingFeatured.forEach((auction) => {
          auction.featured = true
        })
        const allAuctions = [...upcomingFeatured, ...upcoming]

        setAuctions(allAuctions)
        console.log('Fetched live marketplace from API')
      } catch (err) {
        console.error('Error fetching auctions or user data', err)
      }
    }

    fetchData()
  }, [])

  const tabs = [
    { name: `${configData.auction || 'Auction'}`, href: '../auction/#!/' },
    { name: `${configData.marketplace || 'Marketplace'}`, href: '../auction/#!/marketplace' },
    { name: `${configData.private_treaty || 'Private Treaty'}`, href: '../private-treaty/' }
  ]

  return (
    <div className='component_auction-calendar mx-auto max-w-screen-xl p-6 flex flex-col gap-4'>
      <div className='md:border-b border-gray-200 flex flex-col md:flex-row flex-wrap gap-4'>
        <nav className='border-b md:border-none -mb-px flex justify-around md:justify-start' aria-label='Tabs'>
          {tabs.map((tab) => {
            return (
              <a
                key={tab.name}
                className={`${
                  tab.name.toLowerCase() === 'auction' ? 'border-blue-800 text-blue-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } w-auto border-b-2 py-4 text-center font-medium md:px-8`}
                href={tab.href}>
                {tab.name}
              </a>
            )
          })}
        </nav>
        <div className='mb-4 ml-auto w-full md:w-96'>
          <label htmlFor='countryFilter' className='sr-only'>
            Filter by country
          </label>
          <select id='countryFilter' value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} className='border p-2 w-full rounded-md'>
            <option value=''>All Countries</option>
            {[...sliceContext.countries]
              .sort((a, b) => a.localeCompare(b))
              .map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
          </select>
        </div>
      </div>

      <ul className='flex flex-col gap-4'>
        {auctions
          ?.filter((auction) => {
            if (!selectedCountry) return true

            const location = auction?.location || ''
            const country = location.includes(',') ? location.split(',')[1].trim().toLowerCase() : location.trim().toLowerCase()

            return country === selectedCountry.toLowerCase()
          })
          .map((auction) => {
            const isInInitial = initialUuids.has(auction.auctionUuid)
            let auctionUrl = `/auction/#!/auctions/${auction.auctionUuid}`

            if (isInInitial && !slice.primary.show_live_auctions) {
              auctionUrl = `/auction/${auction.title
                .toLowerCase()
                .replace(/ /g, '-')
                .replace(/[^a-zA-Z0-9-]/g, '')
                .replace(/-+/g, '-')}`
            }

            const optimisedLogo = auction?.auctionLogo?.attachmentUrl?.replace('//image/upload/', '/image/upload/ar_4:3,b_rgb:ffffff,c_pad,g_center,f_auto,q_auto:good,w_200/')
            return (
              <li key={auction.auctionUuid} className='bg-white border rounded-md py-4 px-2 shadow-xs relative'>
                {auction.typeMessage && (
                  <div className='flex flex-row gap-1 items-center absolute -top-px -right-px z-10 rounded-md rounded-br-none rounded-tl-none bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10'>
                    {auction.timedType && (
                      <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor' className='size-4'>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z' />
                      </svg>
                    )}
                    {auction.webcastType && (
                      <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor' className='size-4'>
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M9.348 14.652a3.75 3.75 0 0 1 0-5.304m5.304 0a3.75 3.75 0 0 1 0 5.304m-7.425 2.121a6.75 6.75 0 0 1 0-9.546m9.546 0a6.75 6.75 0 0 1 0 9.546M5.106 18.894c-3.808-3.807-3.808-9.98 0-13.788m13.788 0c3.808 3.807 3.808 9.98 0 13.788M12 12h.008v.008H12V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z'
                        />
                      </svg>
                    )}
                    {auction.typeMessage}
                  </div>
                )}
                {auction.featured && (
                  <div className='flex flex-row gap-1 items-center absolute -top-px -left-px z-10 rounded-md rounded-bl-none rounded-tr-none bg-orange-50 px-2 py-1 text-sm font-medium text-orange-700 ring-1 ring-inset ring-orange-700/10'>
                    <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor' className='size-4'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z'
                      />
                    </svg>
                    Featured
                  </div>
                )}
                <EmblaCarousel arrows={true} dots={false} autoplay={false} loop={true}>
                  {auction?.auctionLogo?.attachmentUrl && (
                    <a href={auctionUrl} className='relative min-w-1/2 max-w-1/2 md:min-w-1/5 md:max-w-1/5 flex items-center justify-center px-1'>
                      <img
                        src={optimisedLogo}
                        alt={`${auction?.title} - Logo`}
                        srcSet={`${optimisedLogo?.replace('w_200', 'w_300')} 300w, ${optimisedLogo?.replace('w_200', 'w_400')} 400w, ${optimisedLogo?.replace('w_200', 'w_650')} 650w, ${optimisedLogo?.replace('w_200', 'w_950')} 950w, ${optimisedLogo?.replace('w_200', 'w_1200')} 1200w, ${optimisedLogo?.replace('w_200', 'w_2048')} 2048w`}
                        sizes='(min-width: 1620px) 277px, (min-width: 780px) calc(18.29vw - 16px), calc(50vw - 49px)'
                        className='w-full h-full'
                        loading='lazy'
                      />
                    </a>
                  )}
                  {auction?.upcomingItems.map((item, index) => {
                    item = item?.attachmentModel
                    const optimisedImage = item?.attachmentUrl?.replace('//image/upload/', '/image/upload/ar_4:3,b_rgb:ffffff,c_pad,g_center,f_auto,q_auto:good,w_200/')
                    return (
                      <a
                        href={auctionUrl}
                        className={`relative min-w-1/2 max-w-1/2 md:min-w-1/5 md:max-w-1/5 flex items-center justify-center aspect-[4/3] w-full overflow-hidden px-1 ${'item' + (index === 0 ? ' active' : '')}`}
                        key={`${item?.imageId}-${index}`}>
                        <img
                          src={optimisedImage}
                          alt={`${auction?.title} - ${index}`}
                          srcSet={`${optimisedImage?.replace('w_200', 'w_300')} 300w, ${optimisedImage?.replace('w_200', 'w_400')} 400w, ${optimisedImage?.replace('w_200', 'w_650')} 650w, ${optimisedImage?.replace('w_200', 'w_950')} 950w, ${optimisedImage?.replace('w_200', 'w_1200')} 1200w, ${optimisedImage?.replace('w_200', 'w_2048')} 2048w`}
                          sizes='(min-width: 1620px) 277px, (min-width: 780px) calc(18.29vw - 16px), calc(50vw - 49px)'
                          className='w-full h-full'
                          loading='lazy'
                        />
                      </a>
                    )
                  })}
                </EmblaCarousel>
                <div className='grid md:grid-cols-5 gap-6 p-4'>
                  <div className='md:col-span-4 flex flex-col gap-2'>
                    <div>
                      {auction.endsToday && (
                        <span className='animate-pulse inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-800 ring-1 ring-inset ring-red-600/20'>Ends Today</span>
                      )}
                    </div>
                    <div className='font-semibold'>Ends: {formatTimestampForUser(auction.endOrStartTimestamp)}</div>
                    <a className='flex flex-wrap items-baseline' href={auctionUrl}>
                      <h2 className='mr-1' dangerouslySetInnerHTML={{ __html: auction.title }} />
                      <span className='text-gray-500 items'>
                        ({auction.itemCount} item{auction.itemCount > 1 && 's'})
                      </span>
                    </a>
                    <p dangerouslySetInnerHTML={{ __html: auction.description }} />
                    <span dangerouslySetInnerHTML={{ __html: auction.auctionTitle }} />
                  </div>
                  <div className='flex flex-col justify-between items-stretch gap-4'>
                    <div className='text-right font-semibold'>{auction.location}</div>
                    <div className='x-bidlogix--authenticated-hide'>
                      <a className='button button-brand w-full block text-center' href='/auction/#!/login'>
                        Log in to bid
                      </a>
                    </div>
                    <div className='hidden x-bidlogix--authenticated-show'>
                      <a className='button button-brand w-full block text-center' href={`/auction/#!/auctions/${auction.auctionUuid}/register`}>
                        Register For This Sale
                      </a>
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
      </ul>
    </div>
  )
}

export default AuctionCalendar
